import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, MonitorSmartphone } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { fmtDateTime } from "@/lib/time";

type Device = { device_id: string; label: string; ip?: string; first_seen: string; last_seen: string; current: boolean };

export const DevicesCard = () => {
  const { t, lang } = usePrefs();
  const [rows, setRows] = useState<Device[] | null>(null);
  const load = () => api.get("/auth/devices").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const out = async (d: Device) => {
    if (!window.confirm(t("dev.confirm").replace("{device}", d.label))) return;
    try { await api.delete(`/auth/devices/${d.device_id}`); toast.success(t("dev.done")); load(); } catch (e) { toast.error(errorText(e, t)); }
  };
  const outAll = async () => {
    if (!window.confirm(t("dev.allConfirm"))) return;
    try { const r = await api.post("/auth/devices/signout-others"); toast.success(t("dev.allDone").replace("{n}", String(r.data.count))); load(); } catch (e) { toast.error(errorText(e, t)); }
  };
  if (!rows) return null;
  const others = rows.filter((d) => !d.current).length;
  return (
    <div data-testid="devices-card" className="rounded-3xl border bg-card p-5 space-y-3">
      <div>
        <p className="font-bold flex items-center gap-2"><MonitorSmartphone className="h-5 w-5 text-primary" />{t("dev.title")}</p>
        <p className="text-sm text-muted-foreground">{t("dev.sub")}</p>
      </div>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">{t("dev.empty")}</p> : (
        <ul className="divide-y">
          {rows.map((d) => (
            <li key={d.device_id} data-testid={`device-${d.device_id}`} className="flex items-center gap-3 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm">{d.label}{d.current && <span data-testid="device-current" className="ms-2 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-[11px]">{t("dev.current")}</span>}</p>
                <p className="text-xs text-muted-foreground">{t("dev.last").replace("{time}", fmtDateTime(d.last_seen, lang))} {d.ip && <span dir="ltr">· {d.ip}</span>}</p>
              </div>
              {!d.current && (
                <button onClick={() => out(d)} data-testid={`device-signout-${d.device_id}`} className="h-9 px-3 rounded-full border text-xs font-bold text-rose-600 inline-flex items-center gap-1.5 hover:bg-rose-50 dark:hover:bg-rose-500/10">
                  <LogOut className="h-3.5 w-3.5" />{t("dev.signout")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {others > 0 && (
        <button onClick={outAll} data-testid="devices-signout-all" className="w-full h-11 rounded-full border border-rose-300 text-rose-600 text-sm font-bold inline-flex items-center justify-center gap-2 hover:bg-rose-50 dark:hover:bg-rose-500/10 active:scale-[0.98] transition-transform">
          <LogOut className="h-4 w-4" />{t("dev.all")}
        </button>
      )}
    </div>
  );
};
